import { Controller } from "@hotwired/stimulus"

// Self-hydrating player for the chat audio-message waveform (D8). The static,
// viewer-agnostic markup (play/pause button, 24 seeded bars, "--:--" duration
// placeholder) is byte-identical across broadcast/history/feed; this controller
// only ever reads/binds it — it never changes the DOM shape. Declared via
// `data-controller="chat-audio-player"` on the player root inside
// Chat::Message::Component, so every realtime-appended, morph-restored, or
// Lookbook-rendered node wires itself with no call from chat_controller.
export default class extends Controller {
  static targets = ["audio", "toggle", "playIcon", "pauseIcon", "waveform", "bar", "duration"]

  connect() {
    this.boundHandleLoadedMetadata = this.handleLoadedMetadata.bind(this)
    this.boundHandleTimeUpdate = this.handleTimeUpdate.bind(this)
    this.boundHandlePlay = this.handlePlay.bind(this)
    this.boundHandlePauseOrEnded = this.handlePauseOrEnded.bind(this)
    this.boundHandleEnded = this.handleEnded.bind(this)

    this.audioTarget.addEventListener("loadedmetadata", this.boundHandleLoadedMetadata)
    this.audioTarget.addEventListener("timeupdate", this.boundHandleTimeUpdate)
    this.audioTarget.addEventListener("play", this.boundHandlePlay)
    this.audioTarget.addEventListener("pause", this.boundHandlePauseOrEnded)
    this.audioTarget.addEventListener("ended", this.boundHandleEnded)
  }

  disconnect() {
    // A Turbo Stream replace/remove can tear this controller down mid-playback
    // (e.g. message_updated/message_deleted); without pausing first the audio
    // keeps playing with no UI left to control or show it.
    if (this.hasAudioTarget) this.audioTarget.pause()

    this.audioTarget.removeEventListener("loadedmetadata", this.boundHandleLoadedMetadata)
    this.audioTarget.removeEventListener("timeupdate", this.boundHandleTimeUpdate)
    this.audioTarget.removeEventListener("play", this.boundHandlePlay)
    this.audioTarget.removeEventListener("pause", this.boundHandlePauseOrEnded)
    this.audioTarget.removeEventListener("ended", this.boundHandleEnded)
  }

  toggle() {
    if (this.audioTarget.paused) {
      this.audioTarget.play()
    } else {
      this.audioTarget.pause()
    }
  }

  handlePlay() {
    this.playIconTarget.classList.add("hidden")
    this.pauseIconTarget.classList.remove("hidden")
    this.toggleTarget.setAttribute("aria-label", this.toggleTarget.dataset.pauseLabel)
  }

  handlePauseOrEnded() {
    this.playIconTarget.classList.remove("hidden")
    this.pauseIconTarget.classList.add("hidden")
    this.toggleTarget.setAttribute("aria-label", this.toggleTarget.dataset.playLabel)
  }

  handleEnded() {
    this.handlePauseOrEnded()
    this.audioTarget.currentTime = 0
    this.setProgress(0)
    this.durationTarget.textContent = this.formatTime(this.audioTarget.duration)
  }

  handleLoadedMetadata() {
    this.durationTarget.textContent = this.formatTime(this.audioTarget.duration)
  }

  handleTimeUpdate() {
    const { currentTime, duration } = this.audioTarget
    if (!Number.isFinite(duration) || duration === 0) return

    this.setProgress(currentTime / duration)
  }

  setProgress(fraction) {
    this.waveformTarget.style.setProperty("--progress", String(fraction))

    const filledCount = Math.round(fraction * this.barTargets.length)
    this.barTargets.forEach((bar, index) => {
      bar.classList.toggle("chat-waveform-bar--played", index < filledCount)
    })
  }

  formatTime(seconds) {
    if (!Number.isFinite(seconds)) return "--:--"

    const total = Math.floor(seconds)
    const minutes = Math.floor(total / 60)
    const remainingSeconds = String(total % 60).padStart(2, "0")
    return `${minutes}:${remainingSeconds}`
  }
}
